import { useState, type FormEvent } from 'react';
import { AlertTriangle, LoaderCircle, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { fetchGraphQL } from '@/relay/fetchGraphQL';

const deleteAccountMutation = `
  mutation AccountSettingsDeleteAccount($password: String!, $confirmation: String!) {
    deleteAccount(password: $password, confirmation: $confirmation)
  }
`;

interface GraphQLResult<T> {
  data?: T;
  errors?: Array<{ message?: string }>;
}

export function AccountSettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password || confirmation !== 'DELETE') return;

    setIsDeleting(true);
    try {
      const response = (await fetchGraphQL(
        { text: deleteAccountMutation } as never,
        { password, confirmation },
      )) as GraphQLResult<{ deleteAccount: boolean }>;

      if (response.errors?.length) {
        throw new Error(response.errors[0]?.message || 'Could not delete your account');
      }
      if (response.data?.deleteAccount !== true) {
        throw new Error('Could not delete your account');
      }

      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete your account');
      setIsDeleting(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:py-12">
      <header className="mb-8">
        <p className="text-sm font-medium text-muted-foreground">Account</p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
          Account settings
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage your Day Sheetly account and its personal data.
        </p>
      </header>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Profile</CardTitle>
            <CardDescription>Your account information.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Name</p>
              <p className="mt-1 text-sm text-foreground">{user?.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</p>
              <p className="mt-1 break-all text-sm text-foreground">{user?.email ?? '—'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-destructive/30">
          <CardHeader>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <ShieldAlert className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <CardTitle className="text-xl">Delete account</CardTitle>
                <CardDescription className="mt-2 leading-6">
                  This permanently deletes your account, owned workspaces, projects, time entries,
                  memberships and invites. Data in workspaces owned by another member stays with
                  that workspace, and billing records may be retained by Stripe as required by law.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6 flex gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>This action cannot be undone. Export any reports you need before continuing.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="space-y-2">
                <Label htmlFor="delete-account-password">Current password</Label>
                <Input
                  id="delete-account-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={isDeleting}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-account-confirmation">
                  Type <span className="font-mono">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-account-confirmation"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  disabled={isDeleting}
                  autoCapitalize="characters"
                  spellCheck={false}
                  required
                />
              </div>

              <Button
                type="submit"
                variant="destructive"
                disabled={isDeleting || !password || confirmation !== 'DELETE'}
              >
                {isDeleting && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {isDeleting ? 'Deleting account...' : 'Delete my account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
